import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";

export function HeroSection() {
  return (
    <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4">
      {/* Background decorations */}
      <div className="pointer-events-none absolute inset-0">
        {/* Real Kalocsai pattern — top-right corner */}
        <img
          src="/kalocsai-corner-tr.webp"
          alt=""
          aria-hidden="true"
          className="absolute top-0 right-0 w-[400px] h-[400px] object-contain"
          style={{
            opacity: 0.1,
            maskImage: "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
          }}
        />
        {/* Real Kalocsai pattern — bottom-left corner */}
        <img
          src="/kalocsai-corner-bl.webp"
          alt=""
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-[350px] h-[350px] object-contain"
          style={{
            opacity: 0.08,
            maskImage: "radial-gradient(ellipse at bottom left, black 30%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at bottom left, black 30%, transparent 70%)",
          }}
        />

        {/* Gradient orbs — tinted with folk colors */}
        <div className="absolute -top-1/4 -right-1/4 h-[600px] w-[600px] rounded-full bg-[#6B9FD4]/5 blur-[120px]" />
        <div className="absolute -bottom-1/4 -left-1/4 h-[500px] w-[500px] rounded-full bg-[#D94030]/4 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-5xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/50 px-4 py-1.5 text-sm text-muted-foreground backdrop-blur-sm">
            <span className="h-2 w-2 rounded-full bg-folk-blue animate-pulse" />
            From SZESE E-Sport — Hungarian Precision in Sim Racing
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mb-8"
        >
          <h1 className="sr-only">
            Setups by SZESE — Professional iRacing Car Setups by SZESE E-Sport
          </h1>
          <img
            src="/logo.png"
            alt="Setups by SZESE"
            className="mx-auto h-24 w-auto sm:h-32 md:h-40 lg:h-48"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground md:text-xl"
        >
          Pro iRacing setups made by world-class drivers. Downloaded in seconds.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
        >
          <Button
            asChild
            size="lg"
            className="h-12 bg-folk-blue px-8 text-base font-semibold text-white hover:bg-folk-blue/90"
          >
            <Link
              to="/auth/signin"
              search={{} as any}
              onClick={() => analytics.landingCtaClicked({ location: "hero_browse" })}
            >
              Browse Setups
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-8 text-base">
            <a
              href="#pricing"
              onClick={() => analytics.landingCtaClicked({ location: "hero_pricing" })}
            >
              View Pricing
            </a>
          </Button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <a
          href="#stats"
          className="flex flex-col items-center gap-2 text-muted-foreground/50 transition-colors hover:text-muted-foreground"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="h-4 w-4 animate-bounce" />
        </a>
      </motion.div>
    </section>
  );
}
